import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ChevronsUpDown, Plus, Search } from "lucide-react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import ClientForm from "@/components/client/ClientForm";
import { cn } from "@/lib/utils";

export default function ClientPicker({ value, onChange, disabled, testid = "form-client-picker" }) {
  // value: { id, name } or null
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const inputRef = useRef(null);

  const fetchClients = async () => {
    try {
      const { data } = await api.get("/clients", { params: { limit: 500 } });
      setClients(data.items);
    } catch (err) { logger.error("Failed to load clients:", err); }
  };

  useEffect(() => { fetchClients(); }, []);

  useEffect(() => {
    if (open) {
      // Autofocus after mount
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    } else {
      setQuery("");
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => c.name.toLowerCase().includes(q));
  }, [clients, query]);

  const exactMatch = useMemo(() => {
    if (!query.trim()) return null;
    return clients.find((c) => c.name.toLowerCase() === query.trim().toLowerCase()) || null;
  }, [clients, query]);

  const onCreated = (c) => {
    setClients((cs) => [c, ...cs.filter((x) => x.id !== c.id)]);
    onChange?.(c);
    setShowCreate(false);
    setOpen(false);
    setQuery("");
  };

  const select = (c) => {
    onChange?.(c);
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            className={cn("w-full justify-between", !value && "text-muted-foreground")}
            disabled={disabled}
            data-testid={testid}
          >
            <span className="truncate">{value?.name || "Select client…"}</span>
            <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); }}
        >
          <div className="p-2 border-b border-slate-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search client…"
                className="pl-8 h-9"
                data-testid="client-picker-search"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (filtered.length === 1) select(filtered[0]);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setOpen(false);
                  }
                }}
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto py-1" data-testid="client-picker-list">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-500 text-center">No matching client.</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => select(c)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-100 transition-colors",
                    value?.id === c.id && "bg-blue-50"
                  )}
                  data-testid={`client-picker-item-${c.id}`}
                >
                  <Check className={cn("h-4 w-4 shrink-0", value?.id === c.id ? "opacity-100 text-blue-600" : "opacity-0")} />
                  <span className="truncate flex-1">{c.name}</span>
                  {c.vat_trn && <span className="text-[10px] text-slate-400 font-mono-num shrink-0">{c.vat_trn}</span>}
                </button>
              ))
            )}
          </div>
          <div className="border-t border-slate-200 p-1.5">
            <button
              type="button"
              onClick={() => { setShowCreate(true); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-blue-700 hover:bg-blue-50 transition-colors"
              data-testid="client-picker-new"
            >
              <Plus className="w-4 h-4" />
              <span className="truncate">{query.trim() && !exactMatch ? `Add "${query.trim()}" as new client` : "Add new client"}</span>
            </button>
          </div>
        </PopoverContent>
      </Popover>
      <ClientForm
        open={showCreate}
        onClose={() => setShowCreate(false)}
        defaultName={query.trim()}
        onSaved={onCreated}
      />
    </>
  );
}
